import {
  useAuthoredRows,
  useCreateAuthoredRow,
  useDeleteAuthoredRow,
  useUpdateAuthoredRow,
} from 'components/IAP/hooks/icsAuthoredFormHooks';
import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';

import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import SaveableField from '../SaveableField';
import { ICS261_BOX_LABELS, ICS261_ITEM_COLUMNS } from '../ics261BoxLabels';
import { BoxRowTable, NewCgFormCard, NewCgHeaderBoxes } from './newCgFormBoxes';

/**
 * ICS 261-CG. The sheet has no operational period and no Prepared by box, so
 * the card keeps one tracking list for the incident and versions each save.
 */
const Ics261Card = () => {
  const { effectivePeriodNumber } = useICSContext();
  const { data: sheets = [] } = useAuthoredRows('accountableResourceSheets');
  const { data: items = [] } = useAuthoredRows('accountableResourceItems');
  const createSheet = useCreateAuthoredRow('accountableResourceSheets');
  const updateSheet = useUpdateAuthoredRow('accountableResourceSheets');
  const createItem = useCreateAuthoredRow('accountableResourceItems');
  const updateItem = useUpdateAuthoredRow('accountableResourceItems');
  const deleteItem = useDeleteAuthoredRow('accountableResourceItems');
  const recordVersionSave = useRecordFormVersionSave(
    'ICS_261',
    effectivePeriodNumber
  );
  const onSuccess = () => recordVersionSave.mutate();
  const sheet = sheets[0] ?? null;

  const saveSheet = (patch: Record<string, unknown>) => {
    if (!sheet) {
      createSheet.mutate(patch, { onSuccess });
      return;
    }
    updateSheet.mutate(
      { entityId: String(sheet.sheet_id), ...patch },
      { onSuccess }
    );
  };

  return (
    <NewCgFormCard
      title="Incident Accountable Resource Tracking Sheet ICS-261"
      formname="ICS_261_CG"
      baseId="ICS_261"
      exportLabel="Preview ICS-261"
    >
      <NewCgHeaderBoxes
        labels={ICS261_BOX_LABELS}
        testIdPrefix="ics261"
        trailing={
          <Ics204CardSection
            boxLabel={ICS261_BOX_LABELS.fundingSource}
            testId="ics261-section-funding"
            className="tw:mt-0 tw:h-full tw:min-w-0"
          >
            <SaveableField
              id="ics261-funding-source"
              label="Funding source"
              hideLabel
              value={String(sheet?.funding_source ?? '')}
              onSave={(value) => saveSheet({ funding_source: value })}
            />
          </Ics204CardSection>
        }
      />

      <Ics204CardSection
        boxLabel={ICS261_BOX_LABELS.projectNumber}
        testId="ics261-section-project"
        className="tw:mt-0"
      >
        <SaveableField
          id="ics261-project-number"
          label="Project number"
          hideLabel
          value={String(sheet?.project_number ?? '')}
          onSave={(value) => saveSheet({ project_number: value })}
        />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS261_BOX_LABELS.items}
        testId="ics261-section-items"
        className="tw:mt-0"
      >
        <BoxRowTable
          columns={ICS261_ITEM_COLUMNS}
          rows={items}
          identityColumn="item_id"
          testId="ics261-items-table"
          addLabel="Add item"
          onSave={(entityId, patch) =>
            updateItem.mutate({ entityId, ...patch }, { onSuccess })
          }
          onDelete={(entityId) => deleteItem.mutate(entityId, { onSuccess })}
          onAdd={async () => {
            const created = await createItem.mutateAsync({
              sort_order: items.length,
            });
            onSuccess();
            return String(created?.item_id ?? '');
          }}
        />
      </Ics204CardSection>
    </NewCgFormCard>
  );
};

export default Ics261Card;
